// Records each AI request for the Usage report (Settings → AI engine → Usage).
//
// One record per request the person made: a session, a described workout, a
// programme review. A request can be several provider calls (a truncation
// retry, the refine pass); their tokens and cost are added up first, the same
// way the generation cost shown on a session is (usage.js accumulateUsage).
// Fire and forget: a report that fails to send must never fail the request.

import api, { getToken } from "../../services/api";
import { accumulateUsage } from "./usage";

/** The record to send, or null when the request never reached a provider. */
export const buildUsageRecord = ({ operation, settings, entries, durationMs = null, ok = true }) => {
  const total = accumulateUsage(entries);
  if (!total) return null;
  const provider = total.provider || settings?.provider || null;
  const shareId = settings?.creditShare?.id || null;
  return {
    operation,
    provider,
    model: total.model || settings?.model || null,
    paidBy: provider === "ollama" ? "local" : shareId ? "share" : "own",
    shareId,
    calls: total.calls,
    inputTokens: total.inputTokens,
    outputTokens: total.outputTokens,
    costUsd: total.cost,
    isActual: total.isActual,
    durationMs,
    ok,
  };
};

/** Send a record if signed in; reports live with the account. */
export const recordUsage = (args) => {
  if (!getToken()) return;
  const record = buildUsageRecord(args);
  // Wrapped so nothing the reporting does can throw into the request itself.
  if (record) Promise.resolve().then(() => api.recordAiUsage(record)).catch(() => {});
};

/**
 * Run one AI request, collecting its provider calls through the `onUsage`
 * it is handed, and record it whether it succeeds or fails: a failed request
 * that got a response was still billed.
 */
export const trackUsage = async (operation, settings, run) => {
  const entries = [];
  const started = Date.now();
  try {
    const result = await run((entry) => entries.push(entry));
    recordUsage({ operation, settings, entries, durationMs: Date.now() - started, ok: true });
    return result;
  } catch (error) {
    recordUsage({ operation, settings, entries, ok: false });
    throw error;
  }
};
